// Primitive · Modal
//
// Wraps AntD Modal for portal / focus trap / mask / key handling, and ships
// editorial chrome (icon-tinted square, inline pills, dotted-meta subtitle,
// banner slot, footer-left status) via composed slots.
//
// All visual values come from `src/theme/tokens` — never hardcode.
//
// Locked-down foot-guns:
//   - `destroyOnHidden` always on (prevents form-state leak between opens —
//     see CrudModalShell + 25+ call sites that hand-set this).
//   - `maskClosable` defaults to `false` (project convention — avoid
//     accidental dismiss; opt in for non-destructive views).
//   - `centered` defaults to `true`.
//   - `bodyStyle` is a stable prop name → AntD's `styles.body`.
//   - Footer collapses six AntD props (`okText` / `cancelText` / `onOk` /
//     `okButtonProps` / `cancelButtonProps` / `footer`) into one declarative
//     `actions` array, plus `footerLeft` for status text.

import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Modal as AntModal } from "antd";

import { Button, type ButtonVariant } from "@ds/primitives/Button";
import { Inline } from "@ds/layout/Inline";
import { TextValue } from "@ds/display/TextValue";
import {
  FONT_SIZE,
  LINE_HEIGHT,
  RADIUS,
  SPACE,
  SURFACE,
  TEXT,
  TONE,
  type ToneName,
} from "@ds/tokens/palette";

// ─── Tone for the tinted icon-box ────────────────────────────────────────
//
// Reuses the canonical TONE palette directly — no per-component mapping.

export type ModalIconTone = ToneName;

// ─── Inputs (data props — the model) ─────────────────────────────────────

export interface ModalInput {
  /** Open state — controlled. */
  open: boolean;

  // ─── Header ────────────────────────────────────────────────────────────

  /** Optional tinted icon-box at the start of the title row. The wrapper
   *  renders the box; pass any ReactNode (typically an `@ant-design/icons`
   *  glyph). Set `iconTone` to control the tint. */
  icon?: ReactNode;
  /** Tint for the icon-box. Default `green`. Ignored when `icon` is unset. */
  iconTone?: ModalIconTone;
  /** Title text/element. Renders inline with `pills` in a flex row. */
  title: ReactNode;
  /** Inline pills next to the title (e.g. id + status). Pass a fragment of
   *  `<TonePill>` or `<Tag>` elements. */
  pills?: ReactNode;
  /** Subtitle row beneath the title — typically dotted-separated meta
   *  (e.g. "Holzmann → ArcelorMittal · Złom · 24,12 t"). */
  subtitle?: ReactNode;
  /** Right-aligned slot in the header (status pills, secondary action
   *  buttons). Renders before the close × icon. */
  headerRight?: ReactNode;

  // ─── Banner (between header and body) ──────────────────────────────────

  /** Optional banner row between header and body — typically a tone-coded
   *  alert. Consumer composes the alert visual; the wrapper only allocates
   *  the slot. */
  banner?: ReactNode;

  // ─── Body ──────────────────────────────────────────────────────────────

  /** Body content. */
  body: ReactNode;
  /** Style for the body container — maps to AntD's `styles.body`.
   *  Override to escape the default scroll boundary
   *  (`maxHeight: "calc(100vh - 200px)"`, `overflowY: "auto"`). Set
   *  `{ maxHeight: "none", overflowY: "visible" }` for full-bleed bodies
   *  that manage their own scroll. */
  bodyStyle?: CSSProperties;

  // ─── Sizing ────────────────────────────────────────────────────────────

  /** Width — number = px. Pass `"95vw"` for full-viewport modals.
   *  Default `560` (single-column form). Wide forms = 720; split-pane = 1100. */
  width?: number | string;
  /** Vertical center on the viewport. Default `true`. */
  centered?: boolean;
  /** Show the close (×) icon in the header. Default `true`. */
  closable?: boolean;
  /** Click on the dimmed mask closes the modal. Default `false` — opt in
   *  for non-destructive views (peek, lightbox). */
  maskClosable?: boolean;
  /** Esc closes the modal. Default `true`. */
  keyboard?: boolean;
  /** Inline style on the modal root. Use for `top` overrides etc. */
  style?: CSSProperties;
  /** Distance from viewport top — convenience alias for `style.top`. */
  top?: number | string;
  /** Modal stack level. */
  zIndex?: number;
  /** className escape hatch on the modal root. */
  className?: string;
  /** Optional `data-testid` applied to the modal root for e2e scoping.
   *  Inner testids (`modal-close`, `modal-action-{id}`, `modal-banner`)
   *  emit regardless. See `Modal.e2e.ts` for the matching `ModalLocator`. */
  testId?: string;
  /** Sizing variant.
   *  - `"default"` (default): centered dialog, rounded corners, standard sizing.
   *  - `"fullscreen"`: content fills 100 vh, borderRadius: 0, flex column
   *    layout — use for full-viewport reading/email-preview modals. Sets
   *    `top=0`, `centered=false`, `maskClosable=false` and overrides content
   *    + wrapper styles automatically. */
  size?: "default" | "fullscreen";
}

// ─── Outputs (events emitted — controller hooks) ─────────────────────────

export interface ModalOutput {
  /** Fires when the modal wants to close: × click, Esc, mask click (when
   *  enabled), or any cancel-style action. Consumers must flip `open` to
   *  `false` in response. */
  onClose: () => void;
}

// ─── Side actions (declarative footer) ───────────────────────────────────

export interface ModalAction {
  /** Stable id — used as React key + e2e selector. */
  id: string;
  label: ReactNode;
  /** Visual variant. Defaults: first action = `default`, last = `primary`. */
  variant?: ButtonVariant;
  icon?: ReactNode;
  disabled?: boolean;
  /** External loading state. When `onTrigger` returns a Promise, the
   *  wrapper toggles loading automatically — use this only when the
   *  loading state is owned outside the Modal. */
  loading?: boolean;
  /** Action handler. May return a Promise; while it resolves, the wrapper
   *  sets `loading` on this action's button. */
  onTrigger: () => void | Promise<void>;
}

export interface ModalActions {
  /** Declarative footer. When omitted or empty, no footer is rendered. */
  actions?: ModalAction[];
  /** Left-aligned status text in the footer (e.g. "Bez zmian", "Wymagany
   *  powód zmiany ceny"). Renders as muted prose. */
  footerLeft?: ReactNode;
}

export type ModalProps = ModalInput & ModalOutput & ModalActions;

// ─── Header / footer geometry — composed from SPACE token ─────────────────
// AntD lets us pass `padding` strings, so we compose from the spacing scale
// rather than hardcoding pixel values.
const HEADER_PADDING = `${SPACE.md}px ${SPACE.xl}px`;            // 12px / 24px
const BODY_PADDING   = `${SPACE.lg}px ${SPACE.xl}px`;            // 16px / 24px (visually 20 in-between feels — picked SPACE.lg=16 for tighter density)
const FOOTER_PADDING = `${SPACE.md}px ${SPACE.xl}px`;            // 12px / 24px
const ICON_BOX_SIZE  = SPACE.xxl;                                // 32 — closest token match for the editorial icon-tile

// ─── Helper: icon-box ────────────────────────────────────────────────────

function IconBox({ tone, children }: { tone: ModalIconTone; children: ReactNode }) {
  const t = TONE[tone];
  return (
    <div
      style={{
        flexShrink: 0,
        width: ICON_BOX_SIZE,
        height: ICON_BOX_SIZE,
        borderRadius: RADIUS.lg,
        background: t.softBg,
        border: `1px solid ${t.border}`,
        color: t.text,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: FONT_SIZE.subheading,
        lineHeight: 1,
      }}
    >
      {children}
    </div>
  );
}

// ─── Helper: close button (auto-centered via flex; replaces AntD's
// fixed-position close icon which doesn't align with our editorial header
// padding) ───────────────────────────────────────────────────────────────

function CloseButton({ onClose, closable }: { onClose: () => void; closable: boolean }) {
  if (!closable) return null;
  return (
    <button
      type="button"
      aria-label="Close"
      data-testid="modal-close"
      onClick={onClose}
      style={{
        flexShrink: 0,
        width: SPACE.xl,
        height: SPACE.xl,
        border: "none",
        borderRadius: RADIUS.sm,
        background: "transparent",
        color: TEXT.muted,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: FONT_SIZE.subheading,
        lineHeight: 1,
        padding: 0,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = SURFACE.surfaceHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      ×
    </button>
  );
}

// ─── Helper: composed title ──────────────────────────────────────────────

function ComposedTitle({
  icon,
  iconTone = "green",
  title,
  pills,
  subtitle,
  headerRight,
  closable,
  onClose,
}: Pick<ModalInput, "icon" | "iconTone" | "title" | "pills" | "subtitle" | "headerRight"> & {
  closable: boolean;
  onClose: () => void;
}) {
  return (
    <Inline gap="md" align="center">
      {icon && <IconBox tone={iconTone}>{icon}</IconBox>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display:    "flex",
            flexDirection: "row",
            flexWrap:   "wrap",
            alignItems: "center",
            gap:        SPACE.sm,
            lineHeight: LINE_HEIGHT.tight,
          }}
        >
          <TextValue size="lg" weight="semibold" tone="primary">{title}</TextValue>
          {pills}
        </div>
        {subtitle && (
          <div style={{ marginTop: SPACE.xs / 2 }}>
            <TextValue as="div" size="sm" tone="muted" lineHeight="relaxed">
              {subtitle}
            </TextValue>
          </div>
        )}
      </div>
      {headerRight && (
        <div style={{ display: "flex", alignItems: "center", gap: SPACE.sm, flexShrink: 0 }}>
          {headerRight}
        </div>
      )}
      <CloseButton closable={closable} onClose={onClose} />
    </Inline>
  );
}

// ─── Implementation ──────────────────────────────────────────────────────

export function Modal({
  open,
  icon,
  iconTone = "green",
  title,
  pills,
  subtitle,
  headerRight,
  banner,
  body,
  bodyStyle,
  width = 560,
  centered = true,
  closable = true,
  maskClosable = false,
  keyboard = true,
  style,
  top,
  zIndex,
  className,
  testId,
  size = "default",
  onClose,
  actions,
  footerLeft,
}: ModalProps) {
  const isFullscreen = size === "fullscreen";
  // Per-action loading tracker — keyed by action.id. The wrapper toggles
  // loading when an `onTrigger` returns a Promise so consumers don't have
  // to plumb the loading state themselves.
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  const trigger = async (action: ModalAction) => {
    const result = action.onTrigger();
    if (result instanceof Promise) {
      setBusy((prev) => ({ ...prev, [action.id]: true }));
      try {
        await result;
      } finally {
        setBusy((prev) => ({ ...prev, [action.id]: false }));
      }
    }
  };

  const renderFooter = () => {
    const hasActions = !!(actions && actions.length > 0);
    const hasLeft = !!footerLeft;
    if (!hasActions && !hasLeft) return null;
    return (
      // `modal-footer` testid makes the footer slot addressable from e2e
      // specs via `getByTestId("modal-footer")` — avoids `.ant-modal-footer`.
      // width: 100% — AntD's modal-footer wrapper doesn't stretch its child,
      // so without this the outer flex container would only be as wide as
      // its content, and `flex: 1` on the info div wouldn't actually push
      // the action group to the right edge.
      <Inline
        testId="modal-footer"
        gap="md"
        align="center"
        fullWidth
      >
        {hasLeft && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <TextValue as="div" size="sm" tone="muted" lineHeight="normal">
              {footerLeft}
            </TextValue>
          </div>
        )}
        {hasActions && (
          // Buttons always anchored to the right edge of the footer.
          <div style={{ display: "flex", gap: SPACE.sm, marginLeft: hasLeft ? 0 : "auto" }}>
            {actions.map((action, i) => {
              const variant: ButtonVariant =
                action.variant ??
                (i === actions.length - 1 ? "primary" : "default");
              return (
                <Button
                  key={action.id}
                  label={action.label}
                  variant={variant}
                  icon={action.icon}
                  disabled={action.disabled}
                  loading={action.loading || busy[action.id]}
                  onClick={() => void trigger(action)}
                  testId={`modal-action-${action.id}`}
                />
              );
            })}
          </div>
        )}
      </Inline>
    );
  };

  // Fullscreen preset: top=0, no centering, content spans 100 vh with no
  // border-radius (full-viewport reading / email-preview modals).
  const effectiveTop     = isFullscreen ? 0   : top;
  const effectiveCentered = isFullscreen ? false : centered;

  const mergedRootStyle: CSSProperties | undefined =
    effectiveTop != null
      ? { top: effectiveTop, margin: isFullscreen ? "0 auto" : undefined, maxWidth: isFullscreen ? "100vw" : undefined, paddingBottom: 0, ...style }
      : style;

  // When a banner is provided, render it flush-top inside the body container
  // so it sits between the header bottom-border and the body content (per
  // the editorial design — see _modalShared.ModalChrome.banner).
  // Negative margins cancel the body's own padding so the banner spans
  // edge-to-edge.
  //
  // The outer `modal-body` div makes the body slot addressable from e2e
  // specs via `getByTestId("modal-body")` — avoids the forbidden
  // `.ant-modal-body` class selector (Rule 6).
  const bodyNode = (
    <div
      data-testid="modal-body"
      // Fullscreen: pass the AntD body's height down instead of collapsing to
      // content, so a body slot marked `grow` fills the shell (banner first,
      // then whatever is left). Inert in the default size.
      style={
        isFullscreen
          ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
          : undefined
      }
    >
      {banner ? (
        <>
          <div
            data-testid="modal-banner"
            style={{
              marginLeft:  -SPACE.xl,
              marginRight: -SPACE.xl,
              marginTop:   -SPACE.lg,
              marginBottom: SPACE.lg,
              flexShrink:   0, // fullscreen: never squeezed by the growing body
            }}
          >
            {banner}
          </div>
          {body}
        </>
      ) : (
        body
      )}
    </div>
  );

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      title={
        <div data-testid="modal-title">
          <ComposedTitle
            icon={icon}
            iconTone={iconTone}
            title={title}
            pills={pills}
            subtitle={subtitle}
            headerRight={headerRight}
            closable={closable}
            onClose={onClose}
          />
        </div>
      }
      width={width}
      centered={effectiveCentered}
      // We render our own close button inside the title row (auto-centered
      // via flex). Disable AntD's fixed-position close icon to avoid the
      // double × and the misalignment with our editorial header padding.
      closable={false}
      maskClosable={maskClosable}
      keyboard={keyboard}
      destroyOnHidden
      // Apply the consumer-provided testId on a wrapper around the modal
      // content. `display: contents` keeps AntD's flex/positioning intact.
      // Inner testids (`modal-close`, `modal-action-{id}`, `modal-banner`)
      // emit regardless and remain queryable via the wrapper.
      modalRender={testId ? (node) => <div data-testid={testId} style={{ display: "contents" }}>{node}</div> : undefined}
      footer={renderFooter()}
      styles={{
        // Header: explicit divider + token-driven padding (AntD's default
        // header sometimes drops the bottom border under custom styles).
        header: {
          padding: HEADER_PADDING,
          marginBottom: 0,
          borderBottom: `1px solid ${SURFACE.border}`,
        },
        body: {
          padding: BODY_PADDING,
          background: SURFACE.surface,
          // Modal owns the scroll boundary. Long-content forms render
          // top-to-bottom without bespoke `flex:1 minHeight:0 overflowY:auto`
          // wrappers — when the body exceeds the viewport, this max-height
          // + overflowY pair takes over. Fullscreen modals bypass this
          // entirely; consumers needing custom scroll geometry can override
          // via `bodyStyle` (e.g. `{ maxHeight: "none", overflowY: "visible" }`).
          ...(isFullscreen
            // Fullscreen: the body is the flex child that absorbs whatever
            // the header leaves, and a flex column itself so the body slot
            // can fill it (`flex: 1` on the wrapper below). Without this the
            // body is content-sized and anything meant to fill the shell —
            // a map, a preview pane — has to guess the chrome height.
            ? { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" as const }
            : { maxHeight: "calc(100vh - 200px)", overflowY: "auto" }),
          ...bodyStyle,
        },
        // Footer: soft surface tint + top divider so the action row reads
        // distinctly from the body content.
        footer: {
          padding: FOOTER_PADDING,
          marginTop: 0,
          borderTop: `1px solid ${SURFACE.border}`,
          background: SURFACE.surfaceSoft,
        },
        content: isFullscreen
          ? {
              // Fullscreen: fill the viewport height, no rounding, flex-column
              // so body children can grow with flex: 1.
              padding: 0,
              borderRadius: 0,
              overflow: "hidden",
              height: "100vh",
              display: "flex",
              flexDirection: "column",
            }
          : {
              // Default: rounded corners + padding-zero so header/body/footer
              // styles are the only padding source.
              padding: 0,
              borderRadius: RADIUS.lg,
              overflow: "hidden",
            },
        wrapper: isFullscreen ? { overflow: "hidden" } : undefined,
      }}
      style={mergedRootStyle}
      zIndex={zIndex}
      className={className}
    >
      {bodyNode}
    </AntModal>
  );
}
